import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { api } from '../core/network/api';
import { readRegisterCsrfCookie } from '../core/network/session';
import { IconEye, IconEyeOff, IconLock } from '../common/icons';

import './styles/login.css';

// TSS vite/05 — invite consume (registro / reset de clave, contract
// public-client-channel.md §"Registro / reset por invitación", 0046).
//
// Flow: GET public/register/:token validates the invite (no PII back, just
// { ok, purpose: 'register'|'reset' }) and sets the pre-session CSRF cookie
// csrf_token_register (~10 min). The POST consumes the single-use token with
// { password (min 10), name?, phone? } echoing that cookie in X-CSRF-Token
// (the api interceptor respects the explicit header). Every failure is the
// SAME generic 400 "Enlace inválido o expirado" (anti-enumeración).

const MIN_PASSWORD = 10;

const COPY = {
    register: {
        title: 'Crea tu cuenta',
        hint: 'Define tu contraseña para entrar al portal.',
        submit: 'Crear cuenta',
        success: 'Cuenta creada. Inicia sesión con tu nueva contraseña.',
    },
    reset: {
        title: 'Restablecer contraseña',
        hint: 'Define tu nueva contraseña.',
        submit: 'Guardar contraseña',
        success: 'Contraseña actualizada. Inicia sesión.',
    },
};

export default function RegisterPage() {
    const navigate = useNavigate();
    const { token } = useParams();

    // 'validating' | 'ready' | 'invalid'
    const [phase, setPhase] = useState('validating');
    const [purpose, setPurpose] = useState('register');

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Validate the invite once; the GET also plants csrf_token_register.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const out = await api.get(`public/register/${token}`).then(r => r.data);
                if (cancelled) return;
                setPurpose(out?.purpose === 'reset' ? 'reset' : 'register');
                setPhase('ready');
            } catch {
                if (cancelled) return;
                setPhase('invalid');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const copy = COPY[purpose];
    const mismatch = confirm.length > 0 && password !== confirm;
    const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
    const canSubmit = !submitting && password.length >= MIN_PASSWORD && password === confirm;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const body = { password };
            if (purpose === 'register') {
                if (name.trim()) body.name = name.trim();
                if (phone.trim()) body.phone = phone.trim();
            }
            // Echo the pre-session CSRF cookie explicitly (double-submit, 0046 §8).
            const csrf = readRegisterCsrfCookie();
            await api.post(`public/register/${token}`, body, {
                headers: csrf ? { 'X-CSRF-Token': csrf } : {},
            });
            toast.success(copy.success);
            navigate('/login', { replace: true });
        } catch (err) {
            // 400 genérico (anti-enumeración) · 403 CSRF (cookie vencida ~10 min) · 429 limiter.
            if (err?.status === 403) {
                toast.error('La sesión del enlace expiró. Recarga la página e intenta de nuevo.');
            } else {
                toast.error(err?.message || 'Enlace inválido o expirado');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login">
            <div className="login__panel">
                <main className="login__main">
                    <div className="login__card">
                        <div className="login__brand login__brand--compact">
                            <span className="login__logo-tile">
                                <img className="login__logo login__logo--sm" src="/cowre.png" alt="" aria-hidden="true" />
                            </span>
                            <span className="login__wordmark login__wordmark--compact">COWRE</span>
                        </div>

                        {phase === 'validating' && (
                            <header className="login__head">
                                <h2 className="login__title">Validando enlace…</h2>
                            </header>
                        )}

                        {phase === 'invalid' && (
                            <>
                                <header className="login__head">
                                    <h2 className="login__title">Enlace inválido o expirado</h2>
                                    <p className="login__hint">
                                        Pide a tu asesor un nuevo enlace de acceso.
                                    </p>
                                </header>
                                <button type="button" className="login__submit" onClick={() => navigate('/login')}>
                                    Ir a iniciar sesión
                                </button>
                            </>
                        )}

                        {phase === 'ready' && (
                            <form onSubmit={handleSubmit}>
                                <header className="login__head">
                                    <h2 className="login__title">{copy.title}</h2>
                                    <p className="login__hint">{copy.hint}</p>
                                </header>

                                {purpose === 'register' && (
                                    <>
                                        <label className="login__field">
                                            <span className="login__label">Nombre (opcional)</span>
                                            <span className="login__control">
                                                <input
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    autoComplete="name"
                                                    maxLength={120}
                                                    disabled={submitting}
                                                />
                                            </span>
                                        </label>
                                        <label className="login__field">
                                            <span className="login__label">Teléfono (opcional)</span>
                                            <span className="login__control">
                                                <input
                                                    type="tel"
                                                    value={phone}
                                                    onChange={(e) => setPhone(e.target.value)}
                                                    autoComplete="tel"
                                                    maxLength={40}
                                                    disabled={submitting}
                                                />
                                            </span>
                                        </label>
                                    </>
                                )}

                                <label className="login__field">
                                    <span className="login__label">Contraseña (mínimo {MIN_PASSWORD} caracteres)</span>
                                    <span className="login__control">
                                        <span className="login__control-icon"><IconLock /></span>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            autoComplete="new-password"
                                            minLength={MIN_PASSWORD}
                                            placeholder="••••••••••"
                                            disabled={submitting}
                                            autoFocus
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="login__toggle"
                                            onClick={() => setShowPassword((v) => !v)}
                                            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <IconEyeOff /> : <IconEye />}
                                        </button>
                                    </span>
                                </label>

                                <label className="login__field">
                                    <span className="login__label">Confirmar contraseña</span>
                                    <span className="login__control">
                                        <span className="login__control-icon"><IconLock /></span>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            autoComplete="new-password"
                                            placeholder="••••••••••"
                                            disabled={submitting}
                                            required
                                        />
                                    </span>
                                </label>

                                {(tooShort || mismatch) && (
                                    <p className="login__hint" role="alert">
                                        {tooShort
                                            ? `La contraseña debe tener al menos ${MIN_PASSWORD} caracteres.`
                                            : 'Las contraseñas no coinciden.'}
                                    </p>
                                )}

                                <button type="submit" className="login__submit" disabled={!canSubmit}>
                                    {submitting ? 'Guardando…' : copy.submit}
                                </button>
                            </form>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
